<template>

<div :class="{ 'accordion accordion-flush': !embedded }">
    <div :class="{ 'accordion-item': !embedded }">
        <h2 v-if="!embedded" class="accordion-header">
            <button 
                class="accordion-button"
                :class="{'collapsed': !expanded}" 
                type="button" 
                data-bs-toggle="collapse"
                :data-bs-target="'#attachment-collapse-' + entityKey"
                aria-expanded="false"
                @click="expanded = !expanded"
            >
                <span>Assets</span>
            </button>
        </h2>
        <div :class="{ 'accordion-collapse collapse': !embedded, 'show': expanded || embedded }" :id="'attachment-collapse-' + entityKey">
            <div :class="{ 'accordion-body': !embedded }">

                <div v-if="disableActions" class="alert alert-warning">
                    <i class="fas fa-lock me-2"></i>
                    File uploads are disabled.
                </div>
                <kt-upload 
                    v-else
                    @process="processFiles($event)" 
                    multiple="1"
                ></kt-upload>

                <table class="table table-sm table-striped mt-3" v-if="files.length">
                    <thead v-if="embedded">
                        <tr>
                            <th style="width: 120px">Actions</th>
                            <th>File Name</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="file in files" :key="file.Id">
                            <td>
                                <button 
                                    class="btn btn-sm btn-outline-primary me-1" 
                                    @click="handleView(file)"
                                    title="View/Download"
                                    :disabled="loadingFileId === file.Id"
                                >
                                    <i v-if="loadingFileId === file.Id" class="fa fa-spinner fa-spin"></i>
                                    <i v-else class="fa fa-eye"></i>
                                </button>
                                <button 
                                    class="btn btn-sm btn-outline-success me-1" 
                                    @click="handleDownload(file)"
                                    title="Download"
                                    :disabled="disableActions || loadingFileId === file.Id"
                                >
                                    <i v-if="loadingFileId === file.Id" class="fa fa-spinner fa-spin"></i>
                                    <i v-else class="fa fa-download"></i>
                                </button>
                                <button 
                                    class="btn btn-sm btn-outline-danger" 
                                    @click="showDelete(file)"
                                    title="Delete"
                                    :disabled="disableActions || loadingFileId === file.Id"
                                >
                                    <i class="fa fa-trash"></i>
                                </button>
                            </td>
                            <td>
                                <span class="text-truncate d-inline-block" style="max-width: 300px;" :title="file.Title">
                                    {{ file.Title }}
                                </span>
                                <small v-if="file.Value" class="text-muted ms-2">({{ file.Value }})</small>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="!files.length" class="alert alert-secondary mt-3">
                    <i class="fa fa-file"></i> No files uploaded yet.
                </div>

            </div>
        </div>
    </div>
</div>


</template>

<script setup>
import { ref, watch, onUnmounted, computed } from "vue";
import Api from "@/services/Api";
import Alert from "@/services/Alert";
import AttachmentService from "@/services/AttachmentService";
import KtUpload from "@/components/UI/KtUpload.vue";

const _ = ref({});
const files = ref([]);
const unwatch = [];
const expanded = ref(false);
const loadingFileId = ref(null);

const props = defineProps({
    request: {
        type: Number,
        default: 0
    },

    part: {
        type: Number,
        default: 0
    },

    expense: {
        type: Number,
        default: 0
    },

    mileage: {
        type: Number,
        default: 0
    },

    task: {
        type: Number,
        default: 0
    },

    notification: {
        type: Number,
        default: 0
    },

    technician: {
        type: Number,
        default: 0
    },

    staff: {
        type: String,
        default: ''
    },

    embedded: {
        type: Boolean,
        default: false
    },

    disableActions: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['count-loaded', 'files-updated']);

const entityKey = computed(() =>
{
    if (props.notification) return 'notification-' + props.notification;
    if (props.technician || props.staff) return 'pending-' + (props.technician || props.staff);
    return 'request-' + props.request;
});

const load = async () =>
{
    if (props.notification)
    {
        files.value = await Api.loadNotificationFiles(props.notification);
    }
    else if (props.technician || props.staff)
    {
        files.value = await Api.loadPendingNotificationFiles(props.technician || 0, props.staff || '');
    }
    else
    {
        // todo ... link the files to notes / parts / etc ...
        files.value = await Api.loadRequestFiles(props.request);
    }

    // Emit count for parent components (e.g., JobForm tab counts)
    emit('count-loaded', files.value?.length || 0);
    emit('files-updated', files.value || []);
};

const handleView = async (file) =>
{
    if (loadingFileId.value) return; // Prevent concurrent operations
    
    loadingFileId.value = file.Id;
    try
    {
        // For web-viewable files, open in new tab using blob URL (preserves auth)
        if (AttachmentService.isWebViewable(file.Title))
        {
            await AttachmentService.viewFileInTab(file.Id, file.Value);
        }
        else
        {
            // For non-viewable files, trigger download
            await AttachmentService.downloadFile(file.Id, file.Title);
        }
    }
    catch (err)
    {
        console.error('Failed to view/download file:', err);
        Alert.warning('Failed to open file: ' + (err?.message || 'Unknown error'));
    }
    finally
    {
        loadingFileId.value = null;
    }
};

const handleDownload = async (file) =>
{
    if (loadingFileId.value) return; // Prevent concurrent operations
    
    loadingFileId.value = file.Id;
    try
    {
        await AttachmentService.downloadFile(file.Id, file.Title);
    }
    catch (err)
    {
        console.error('Failed to download file:', err);
        Alert.warning('Failed to download file: ' + (err?.message || 'Unknown error'));
    }
    finally
    {
        loadingFileId.value = null;
    }
};

const showDelete = async (file) =>
{
    Alert.confirm('Are you sure you want to delete "' + file.Title + '"?', async () => {
        await Api.deleteFile(file.Id);
        await load();
    });
};

const processFiles = async (filesToUpload) =>
{
    for (let i = 0; i < filesToUpload.length; i++)
    {
        let file = filesToUpload[i];
        var dta = new FormData();
        dta.append("File", file);

        let type = "request";

        if (props.part)
        {
            type = "part";
            dta.append("RefId", props.part);
        }

        else if (props.expense)
        {
            type = "expense";
            dta.append("RefId", props.expense);
        }

        else if (props.mileage)
        {
            type = "mileage";
            dta.append("RefId", props.mileage);
        }

        else if (props.task)
        {
            type = "task";
            dta.append("RefId", props.task);
        }

        else if (props.notification || props.technician || props.staff)
        {
            type = "notification";
            dta.append("RefId", props.notification || 0);

            if (props.technician)
            {
                dta.append("OwnerId", props.technician);
            }

            if (props.staff)
            {
                dta.append("EmployeeId", props.staff);
            }
        }

        dta.append("Type", type);
        dta.append("RequestId", props.request);

        // do we need anything else ...
        await Api.saveFile(dta);   
    }
    
    await load();
};

unwatch.push(watch(() =>
{
    return [props.request, props.notification, props.technician, props.staff];
}, async (nv, ov) => 
{
    const anySet = nv.some(v => v !== 0 && v !== '');
    if (anySet)
    {
        await load();
    }
}, { immediate: true }));

onUnmounted(() =>
{
    unwatch.forEach((un) =>
    {
        un();
    });
});


</script>